import {useEffect, useRef, type PropsWithChildren} from 'react'
import {Box, Label, type LabelProps, merge} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {testIdProps} from '@github-ui/test-id-props'
// eslint-disable-next-line no-restricted-imports
import {iterateFocusableElements} from '@primer/behaviors/utils'

export type NestedListItemTrailingBadgeProps = PropsWithChildren<Pick<LabelProps, 'variant' | 'size'>> & {
  /**
   * Text that is shown as a visible Primer Label and as visually hidden text for screen readers.
   */
  title?: string

  /**
   * Styling for the div surrounding the label
   */
  containerSx?: BetterSystemStyleObject
}

const defaultContainerSx: BetterSystemStyleObject = {display: 'inline', mr: 1}

export const NestedListItemTrailingBadge = ({
  title,
  containerSx,
  children,
  ...props
}: NestedListItemTrailingBadgeProps) => {
  const trailingBadgesContainerRef = useRef(null)

  // Suppression of secondary info and actions
  // https://ui.githubapp.com/storybook/?path=/docs/recipes-nestedlistview-documentation-accessibility-listitem--docs#suppression-of-secondary-info-and-actions
  useEffect(() => {
    if (trailingBadgesContainerRef.current) {
      const elements = [...iterateFocusableElements(trailingBadgesContainerRef.current)]
      for (const el of elements) {
        el.tabIndex = -1 // Suppresses the completion pill and labels
      }
    }
  }, [])

  return (
    <Box
      {...testIdProps('nested-listview-item-trailing-badge')}
      className="trailing-badge"
      sx={containerSx ? merge<BetterSystemStyleObject>(defaultContainerSx, containerSx) : defaultContainerSx}
      aria-hidden
      ref={trailingBadgesContainerRef}
    >
      {children ? (
        children
      ) : (
        <Label sx={{overflow: 'hidden', display: 'inline flex'}} {...props}>
          <Box as="span" sx={{textOverflow: 'ellipsis', overflow: 'hidden', lineHeight: 'normal'}}>
            {title}
          </Box>
        </Label>
      )}
    </Box>
  )
}

NestedListItemTrailingBadge.displayName = 'NestedListItemTrailingBadge'
